import AppStoreBadge from '@/components/common/AppStoreBadge';
import PlayStoreBadge from '@/components/common/PlayStoreBadge';
import { PLAYABLE_CLASS_COUNT } from '@/lib/constants';

function Section({
  title,
  children,
  alternate,
}: {
  title?: string;
  children: React.ReactNode;
  alternate?: boolean;
}) {
  return (
    <section
      className={`relative flex items-center justify-center px-6 py-24 sm:py-32 overflow-hidden ${
        alternate ? 'bg-background/40' : ''
      }`}
    >
      {/* Gold separator line */}
      <div
        className="absolute bottom-4 left-0 right-0 h-px"
        style={{
          background:
            'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
        }}
      />

      <div className="relative z-10 flex flex-col gap-4 max-w-5xl w-full text-center">
        {title && (
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-character text-foreground">
            {title}
          </h2>
        )}
        <div className="text-lg sm:text-xl font-medium text-foreground/70 leading-relaxed space-y-4">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <>
      {/* Hero */}
      <Section title="About Polymyth">
        <p>
          Polymyth is a role-playing games platform that empowers players to adventure from anywhere
          and on any device.
        </p>
        <p>
          Compatible with fifth edition, Polymyth simplifies and automates the technical side of
          role-playing, making it accessible and enjoyable for everyone.
        </p>
        <p>
          Polymyth is not a replacement for tabletop gaming. Our vision is to broaden the reach of
          role-playing games to new audiences who find the hobby too hard and complicated to enjoy.
        </p>
      </Section>

      {/* Free to start */}
      <Section title="Free to Start" alternate>
        <p>
          Polymyth is open to everyone. No invite code, no waiting list. Head to the{' '}
          <a href="/" className="text-primary underline">
            home page
          </a>
          , create an account, and start playing right away, free to start.
        </p>
        <p>
          The Polymyth Beta includes two exclusive adventure modules built for Polymyth,
          Goldvale&apos;s Last Hope and The Bleeding Gate, for players level 1–3. We plan to release
          additional exclusive adventures as the program evolves.
        </p>

        <div className="flex flex-col items-center gap-3 pt-8">
          <h3 className="text-2xl sm:text-3xl font-character text-foreground sm:whitespace-nowrap">
            Play on phone or tablet
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <AppStoreBadge />
            <PlayStoreBadge />
          </div>
        </div>

        <h3 className="text-2xl sm:text-3xl font-character text-foreground pt-4">Beta Features</h3>
        <ul className="text-left space-y-3 mx-auto max-w-lg">
          {[
            'AI-powered narration and game mastering in real time',
            'Full SRD combat compatible with fifth edition: the mechanics resolve for you automatically',
            `${PLAYABLE_CLASS_COUNT} playable classes, species, and backgrounds`,
            'Solo play with AI companion NPCs — form a party, recruit members, lead and grow it through playing',
            'AI-driven NPCs and monsters with unique personalities and capabilities',
            'An exclusive adventure module with quests, riddles, combat encounters, and boss fights',
            'Character progression with leveling and XP rewards',
            'Training Grounds arena for dueling and practice',
            'No prior rules knowledge needed to play',
            'Play whenever you like and as long as you like — no game master or party members scheduling needed',
            'Works on any device directly from your browser — no install needed',
            'Hours of fun free play — all we ask is your feedback',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* The Team */}
      <Section title="The Team">
        <p>
          We're a self-funded team of passionate role-playing games enthusiasts and engineers
          building the future of online adventures. Polymyth is our love letter to the game that
          brought us together.
        </p>
      </Section>

      {/* Legal */}
      <Section title="Legal" alternate>
        <p>Polymyth is compatible with fifth edition.</p>
        <p>
          This work includes material from the System Reference Document 5.1 ("SRD 5.1") by Wizards
          of the Coast LLC, available{' '}
          <a
            href="https://dnd.wizards.com/resources/systems-reference-document"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            here
          </a>
          . The SRD 5.1 is licensed under the Creative Commons Attribution 4.0 International
          License, available{' '}
          <a
            href="https://creativecommons.org/licenses/by/4.0/legalcode"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            here
          </a>
          .
        </p>
        <p>
          This work also includes material from the System Reference Document 5.2.1 ("SRD 5.2.1") by
          Wizards of the Coast LLC, available{' '}
          <a
            href="https://www.dndbeyond.com/srd"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            here
          </a>
          . The SRD 5.2.1 is licensed under the Creative Commons Attribution 4.0 International
          License, available{' '}
          <a
            href="https://creativecommons.org/licenses/by/4.0/legalcode"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            here
          </a>
          .
        </p>
      </Section>
    </>
  );
}
